// components
import { Box, Tooltip } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { IconContainer } from 'components/shared/Header/Header.styles';
import {
  StyledBoxCenterExtraSmallGap,
  StyledBoxFlexColumnJustifyCenterFullWidth
} from 'components/shared/Box/Box.styles';
import { StyledTypographyEllipsisNormal } from 'components/shared/Typography/Typography.styles';
import { StyledEntityColorIndicator } from '../hooks/useGetColumns.styles';
import TooltipContent from './TooltipContent';

// hooks
import { useTranslation } from 'react-i18next';

// types
import type {
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import type { EmployeeNameFormatTypes } from '../RosterPlan.types';

// utils
import dayjs from 'dayjs';
import theme from 'config/theme';
import { formatEmployeeName } from '../hooks/useGetColumns.utils';

const EmployeeCell = ({
  params,
  showSourceCostCenter,
  showCommitmentCostCenter,
  employeeNameFormat
}: {
  params: GridRenderCellParams<
    GridValidRowModel,
    any,
    any,
    GridTreeNodeWithRender
  >;
  showSourceCostCenter?: boolean;
  showCommitmentCostCenter?: boolean;
  employeeNameFormat?: EmployeeNameFormatTypes;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });
  const { row } = params;

  const isSourceAndCommitmentIsSame =
    row.customerId === row.benefits?.costCenterId;
  const benefits = row.benefits || {};
  const functionColor = benefits?.functionColor;

  const tooltipEmployeeContent = [
    {
      label: t('dataGrid.tooltip.number'),
      value: `${row.number} (${row.types
        .map((type: string) => t(`dataGrid.tooltip.types.${type}`))
        .join(', ')})`
    },
    {
      label: t('dataGrid.tooltip.workAgreement'),
      value: (
        <>
          <Box>{row.workAgreementNr}</Box>
          {dayjs(row.workAgreementStartDate).format('DD-MM-YYYY')} -{' '}
          {dayjs(row.workAgreementEndDate).format('DD-MM-YYYY')}
        </>
      )
    },
    {
      label: t('dataGrid.tooltip.classification'),
      value: `${dayjs(benefits?.startDate).format('DD-MM-YYYY')} - ${dayjs(
        benefits?.endDate
      ).format('DD-MM-YYYY')}`
    },
    {
      label: t('dataGrid.tooltip.weeklyHours'),
      value: benefits?.weekHoursMin?.toFixed(2)
    },
    {
      label: t('dataGrid.tooltip.source'),
      value: benefits?.costCenterNameShort
    },
    {
      label: t('dataGrid.tooltip.commitment'),
      value: row.customerNameShort
    },
    {
      label: t('dataGrid.tooltip.function'),
      value: benefits?.functionName
    },
    {
      label: t('dataGrid.tooltip.homePhone'),
      value: row.phone?.defNumber
    },
    {
      label: t('dataGrid.tooltip.mobilePhone'),
      value: row.phone?.smsNumber
    }
  ];

  const employeeName = formatEmployeeName(row, employeeNameFormat);

  return (
    <StyledBoxFlexColumnJustifyCenterFullWidth>
      <Tooltip
        arrow
        title={<TooltipContent content={tooltipEmployeeContent} />}
      >
        <Box>
          {functionColor && (
            <StyledEntityColorIndicator functionColor={functionColor} />
          )}
          <StyledTypographyEllipsisNormal variant="body1">
            {employeeName || '—'}
          </StyledTypographyEllipsisNormal>
          <StyledBoxCenterExtraSmallGap>
            {showSourceCostCenter && (
              <StyledTypographyEllipsisNormal
                variant="body2"
                color="textSecondary"
              >
                {params.row.benefits?.costCenterNameShort || '—'}
              </StyledTypographyEllipsisNormal>
            )}

            {!isSourceAndCommitmentIsSame &&
              showSourceCostCenter &&
              showCommitmentCostCenter && (
                <>
                  <IconContainer
                    iconColor={theme.palette.text.secondary}
                    iconSize="SMALL"
                  >
                    <Icon type="arrow-right" color="inherit" />
                  </IconContainer>
                  <StyledTypographyEllipsisNormal
                    variant="body2"
                    color="textSecondary"
                  >
                    {params.row?.customerNameShort || '—'}
                  </StyledTypographyEllipsisNormal>
                </>
              )}
          </StyledBoxCenterExtraSmallGap>
        </Box>
      </Tooltip>
    </StyledBoxFlexColumnJustifyCenterFullWidth>
  );
};

export default EmployeeCell;
